import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Button } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, HelpCircle } from "lucide-react";
import { useChecklistQuery, useUpdateChecklist } from "../api/queries";
import { ChecklistErrorState } from "../components/ChecklistErrorState";
import { ChecklistLoadingState } from "../components/ChecklistLoadingState";
import { ChecklistNotFoundState } from "../components/ChecklistNotFoundState";
import {
  ChecklistFormTour,
  useChecklistFormTour,
} from "../tour/ChecklistFormTour";
import { ChecklistForm } from "../ui/ChecklistForm";
import { validateChecklistId } from "../utils/checklist-id";

export function ChecklistEditPage() {
  const params = useParams({ strict: false });
  const idParam = params.id;
  const nav = useNavigate();

  const checklistId = validateChecklistId(idParam);

  const checklist = useChecklistQuery(checklistId ?? "");
  const updateChecklist = useUpdateChecklist(checklistId ?? "");
  const formTour = useChecklistFormTour();

  if (!checklistId) {
    return <ChecklistNotFoundState />;
  }

  if (checklist.isLoading) {
    return <ChecklistLoadingState />;
  }

  if (!checklist.data) {
    return <ChecklistErrorState />;
  }

  const checklistData = checklist.data;

  return (
    <>
      <Header />
      <Main>
        <div className="space-y-6">
          {/* Header Section */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-bold text-2xl">ແກ້ໄຂ Checklist</h1>
              <p className="mt-1 text-muted-foreground">
                ແກ້ໄຂຂໍ້ມູນ Checklist: {checklistData.title}
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={formTour.startTour}>
                <HelpCircle className="mr-2 h-4 w-4" />
                ເລີ່ມ Tour
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  nav({
                    to: "/app/checklists/$id",
                    params: { id: checklistId },
                  });
                }}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                ກັບ
              </Button>
            </div>
          </div>

          {/* Edit Form Section */}
          <ChecklistForm
            initialValues={
              checklistData
                ? {
                    title: checklistData.title,
                    description: checklistData.description ?? undefined,
                    typeId: checklistData.typeId,
                    departmentId: checklistData.departmentId ?? undefined,
                    status: checklistData.status,
                    scheduleType: checklistData.scheduleType,
                    scheduleFrequency:
                      checklistData.scheduleFrequency ?? undefined,
                    scheduleDayOfWeek:
                      checklistData.scheduleDayOfWeek ?? undefined,
                    scheduleDayOfMonth:
                      checklistData.scheduleDayOfMonth ?? undefined,
                    requireAllItems: checklistData.requireAllItems,
                    requirePhoto: checklistData.requirePhoto,
                    requireNotesOnFail: checklistData.requireNotesOnFail,
                    items:
                      checklistData.items?.map(
                        (item: {
                          id: string;
                          title: string;
                          description: string | null;
                          order: number;
                          isRequired: boolean;
                          allowNotes: boolean;
                          formId?: string | null;
                        }) => ({
                          id: item.id,
                          title: item.title,
                          description: item.description ?? null,
                          order: item.order,
                          isRequired: item.isRequired,
                          allowNotes: item.allowNotes,
                          formId: item.formId ?? null,
                        }),
                      ) ?? [],
                  }
                : undefined
            }
            onSubmit={async (vals) => {
              await updateChecklist.mutateAsync({
                title: vals.title,
                description: vals.description ?? undefined,
                typeId: vals.typeId,
                departmentId: vals.departmentId ?? undefined,
                status: vals.status,
                scheduleType: vals.scheduleType,
                scheduleFrequency:
                  vals.scheduleType === "scheduled"
                    ? (vals.scheduleFrequency ?? undefined)
                    : undefined,
                scheduleDayOfWeek:
                  vals.scheduleType === "scheduled" &&
                  vals.scheduleFrequency === "weekly"
                    ? (vals.scheduleDayOfWeek ?? undefined)
                    : undefined,
                scheduleDayOfMonth:
                  vals.scheduleType === "scheduled" &&
                  vals.scheduleFrequency === "monthly"
                    ? (vals.scheduleDayOfMonth ?? undefined)
                    : undefined,
                requireAllItems: vals.requireAllItems,
                requirePhoto: vals.requirePhoto,
                requireNotesOnFail: vals.requireNotesOnFail,
                items: vals.items?.map((item, index) => ({
                  ...item,
                  order: item.order ?? index,
                })),
              });
              nav({ to: "/app/checklists/$id", params: { id: checklistId } });
            }}
            submitting={updateChecklist.isPending}
          />
        </div>

        <ChecklistFormTour
          run={formTour.run}
          onCallback={formTour.handleJoyrideCallback}
        />
      </Main>
    </>
  );
}
